import React from 'react';
import Icon from '../../../components/AppIcon';

const ProcessingSummaryStats = ({ stats }) => {
  const statCards = [
    {
      id: 'total_processed',
      label: 'Products Processed',
      value: stats?.totalProcessed?.toLocaleString() || '0',
      icon: 'Package',
      color: 'text-primary',
      bgColor: 'bg-primary bg-opacity-10'
    },
    {
      id: 'exact_matches',
      label: 'Exact Matches',
      value: stats?.exactMatches?.toLocaleString() || '0',
      icon: 'CheckCircle2',
      color: 'text-success',
      bgColor: 'bg-success bg-opacity-10',
      percentage: stats?.totalProcessed > 0 ? ((stats?.exactMatches / stats?.totalProcessed) * 100)?.toFixed(1) : '0'
    },
    {
      id: 'fuzzy_matches',
      label: 'AI Matches',
      value: stats?.fuzzyMatches?.toLocaleString() || '0',
      icon: 'Brain',
      color: 'text-primary',
      bgColor: 'bg-primary bg-opacity-10',
      percentage: stats?.totalProcessed > 0 ? ((stats?.fuzzyMatches / stats?.totalProcessed) * 100)?.toFixed(1) : '0'
    },
    {
      id: 'ambiguous_matches',
      label: 'Need Review',
      value: stats?.ambiguousMatches?.toLocaleString() || '0',
      icon: 'AlertTriangle',
      color: 'text-warning',
      bgColor: 'bg-warning bg-opacity-10',
      percentage: stats?.totalProcessed > 0 ? ((stats?.ambiguousMatches / stats?.totalProcessed) * 100)?.toFixed(1) : '0'
    },
    {
      id: 'no_matches',
      label: 'No Matches',
      value: stats?.noMatches?.toLocaleString() || '0',
      icon: 'XCircle',
      color: 'text-muted-foreground',
      bgColor: 'bg-muted',
      percentage: stats?.totalProcessed > 0 ? ((stats?.noMatches / stats?.totalProcessed) * 100)?.toFixed(1) : '0'
    },
    {
      id: 'processing_speed',
      label: 'Processing Speed',
      value: `${stats?.processingSpeed || 0}/sec`,
      icon: 'Zap',
      color: 'text-accent',
      bgColor: 'bg-accent bg-opacity-10'
    }
  ];

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground mb-4">Processing Summary</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards?.map((card) => (
          <div key={card?.id} className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between mb-3">
              <div className={`p-2 rounded-lg ${card?.bgColor}`}>
                <Icon name={card?.icon} size={20} className={card?.color} />
              </div>
              {card?.percentage && (
                <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                  {card?.percentage}%
                </span>
              )}
            </div>
            
            <div className="space-y-1">
              <div className="text-2xl font-bold text-foreground">
                {card?.value}
              </div>
              <div className="text-sm text-muted-foreground">
                {card?.label}
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Performance Metrics */}
      <div className="bg-card border border-border rounded-lg p-4 mt-6">
        <h4 className="text-md font-semibold text-foreground mb-4">System Performance</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Memory Usage</span>
              <span className="text-sm font-medium text-foreground">
                {stats?.memoryUsage || 0}%
              </span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-300 ${
                  (stats?.memoryUsage || 0) > 80 ? 'bg-error' : 
                  (stats?.memoryUsage || 0) > 60 ? 'bg-warning' : 'bg-success'
                }`}
                style={{ width: `${Math.min(stats?.memoryUsage || 0, 100)}%` }}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">CPU Usage</span>
              <span className="text-sm font-medium text-foreground">
                {stats?.cpuUsage || 0}%
              </span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-300 ${
                  (stats?.cpuUsage || 0) > 80 ? 'bg-error' : 
                  (stats?.cpuUsage || 0) > 60 ? 'bg-warning' : 'bg-success'
                }`}
                style={{ width: `${Math.min(stats?.cpuUsage || 0, 100)}%` }}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Queue Size</span>
              <span className="text-sm font-medium text-foreground">
                {stats?.queueSize?.toLocaleString() || '0'}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={14} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Est. {stats?.estimatedTimeRemaining || '0m'} remaining
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Match Quality Distribution */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h4 className="text-md font-semibold text-foreground mb-4">Match Quality Distribution</h4>
        
        <div className="space-y-3">
          {[
            { label: 'High Confidence (95-100%)', value: stats?.highConfidenceMatches || 0, color: 'bg-success' },
            { label: 'Medium Confidence (85-94%)', value: stats?.mediumConfidenceMatches || 0, color: 'bg-primary' },
            { label: 'Low Confidence (75-84%)', value: stats?.lowConfidenceMatches || 0, color: 'bg-warning' },
            { label: 'Very Low (&lt;75%)', value: stats?.veryLowConfidenceMatches || 0, color: 'bg-error' }
          ]?.map((item, index) => {
            const percentage = stats?.totalProcessed > 0 ? (item?.value / stats?.totalProcessed) * 100 : 0;
            return (
              <div key={index} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground" dangerouslySetInnerHTML={{ __html: item?.label }} />
                  <span className="font-medium text-foreground">
                    {item?.value?.toLocaleString()} ({percentage?.toFixed(1)}%)
                  </span>
                </div>
                <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${item?.color} transition-all duration-500`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProcessingSummaryStats;